import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';

/**
 * DeliveryTimePickerComponent — time input constrained to vendor hours
 * and the meeting window.
 *
 * Computes min/max bounds from vendor operating hours and meeting
 * start/end. Defaults to 15 minutes before meeting start if no value
 * is set.
 */
@Component({
  selector: 'ct-delivery-time-picker',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoPipe],
  template: `
    <div class="dtp">
      <label class="dtp__label">{{ 'catering.customer.delivery_time_picker.label' | transloco }}</label>
      <input
        type="time"
        class="dtp__input"
        [value]="timeString()"
        (change)="onTimeChange($any($event.target).value)"
        [min]="minTime()"
        [max]="maxTime()" />
      @if (vendorCloseTime()) {
        <span class="dtp__hint">{{ 'catering.customer.delivery_time_picker.closes_at' | transloco: { time: vendorCloseTime() } }}</span>
      }
    </div>
  `,
  styles: [`
    :host { display: block; }

    .dtp {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .dtp__label {
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--text-tertiary);
    }

    .dtp__input {
      width: 100%;
      padding: 8px 12px;
      font-size: 12px;
      font-family: inherit;
      font-variant-numeric: tabular-nums;
      color: var(--text);
      background: var(--surface-2);
      border: 1px solid var(--border-low);
      border-radius: var(--radius-sm);
      outline: none;
      transition: border-color 0.15s ease;

      &:focus {
        border-color: var(--color-primary);
      }
    }

    .dtp__hint {
      font-size: 10px;
      color: var(--text-tertiary);
    }

    @media (prefers-reduced-motion: reduce) {
      .dtp__input {
        transition: none;
      }
    }
  `],
})
export class DeliveryTimePickerComponent {
  readonly value = input.required<Date | null>();
  readonly meetingStart = input.required<Date | null>();
  readonly meetingEnd = input.required<Date | null>();
  readonly vendorOpenTime = input.required<string | null>();
  readonly vendorCloseTime = input.required<string | null>();
  readonly timeChanged = output<Date>();

  /** Format current value (or default) as HH:mm string for the input */
  protected readonly timeString = computed(() => {
    const v = this.value();
    if (v) return this.formatHHmm(v);

    // Default: 15 minutes before meeting start
    const start = this.meetingStart();
    if (start) {
      const defaultTime = new Date(start.getTime() - 15 * 60 * 1000);
      return this.formatHHmm(defaultTime);
    }

    return '';
  });

  /** Min bound: max of vendor open time and meeting start */
  protected readonly minTime = computed(() => {
    const vendorOpen = this.vendorOpenTime();
    const start = this.meetingStart();
    const startStr = start ? this.formatHHmm(start) : null;

    if (vendorOpen && startStr) {
      return vendorOpen > startStr ? vendorOpen : startStr;
    }
    return vendorOpen ?? startStr ?? '';
  });

  /** Max bound: min of vendor close time and meeting end */
  protected readonly maxTime = computed(() => {
    const vendorClose = this.vendorCloseTime();
    const end = this.meetingEnd();
    const endStr = end ? this.formatHHmm(end) : null;

    if (vendorClose && endStr) {
      return vendorClose < endStr ? vendorClose : endStr;
    }
    return vendorClose ?? endStr ?? '';
  });

  /** Handle user time change from the input */
  protected onTimeChange(timeStr: string): void {
    if (!timeStr) return;
    const [h, m] = timeStr.split(':').map(Number);
    const dt = new Date();
    dt.setHours(h, m, 0, 0);
    this.timeChanged.emit(dt);
  }

  private formatHHmm(date: Date): string {
    const h = String(date.getHours()).padStart(2, '0');
    const m = String(date.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  }
}
